import { useEffect, useRef, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { RootState } from "../../../store/root-reducer";
import { isScopeLoading } from "../../../store/scope-loading/helpers";
import TableGrid from "../../../components/TableGrid";
import ContractViewModal from "./Modals/ContractViewModal";
import { getContractRecords } from "../../../store/user/actions";
import { ContractStatus, UserScope } from "../../../store/user/types";
import { convertToCRMDate, formatDate } from "../../../helper";

const statusbg = {
  [ContractStatus.Active]:
    "bg-green-200 text-green-600 border border-green-600",
  [ContractStatus.Expired]: "bg-sky-200 text-sky-600 border border-sky-600",
  [ContractStatus.Cancelled]: "bg-red-200 text-red-600 border border-red-600",
  [ContractStatus.CancelRequestDealerReview]:
    "bg-yellow-200 text-yellow-600 border border-yellow-600",
  [ContractStatus.CancelRequestConsumerReview]:
    "bg-indigo-200 text-indigo-600 border border-indigo-600",
};

function Contracts({ data, loading, info, associated_dealers }: any) {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState({
    show: false,
    data: {},
  });
  const searchDataRef = useRef(null);

  function close() {
    setIsOpen({ show: false, data: {} });
  }

  useEffect(() => {
    if (data?.length === 0) {
      dispatch(getContractRecords({ page: 1 }));
    }
    return () => {};
  }, [data?.length, dispatch]);

  const loadData = (
    page: number = 1,
    per_page: number = 20,
    values?: any,
    clearSearch: boolean = false
  ) => {
    const searchPayload = values
      ? Object.keys(values).reduce((acc, key) => {
          if (values[key] && key === "contract_purchase_date") {
            acc[key] = convertToCRMDate(values[key]);
          } else if (key === "dealer_id" && values[key] === "all") {
            acc[key] = associated_dealers.map((d: any) => d.id).join(",");
          } else if (values[key]) {
            acc[key] = values[key];
          }
          return acc;
        }, {})
      : null;
    if (searchPayload) searchDataRef.current = searchPayload;
    if (clearSearch) searchDataRef.current = null;
    dispatch(
      getContractRecords({
        search: searchPayload || searchDataRef.current,
        page,
        per_page,
      })
    );
  };

  const tableHeaders = [
    { label: "Contract Number", key: "contractNumber" },
    {
      label: "Status",
      key: "status",
      format: (val: any) => (
        <div
          className={`inline-block items-center justify-center  py-0.5 px-3 rounded-2xl text-[12px] ${statusbg[val]}`}
          role="presentation"
        >
          <span>{val || "N/A"}</span>
        </div>
      ),
    },
    {
      label: "Delivery Date",
      key: "deliveryDate",
      format: (val: any) => formatDate(val, "N/A"),
    },
    {
      label: "Purchase Date",
      key: "purchaseDate",
      format: (val: any) => formatDate(val, "N/A"),
    },
    { label: "Customer Name", key: "customerName" },
    { label: "Customer Address", key: "customerAddress" },
    { label: "Invoice", key: "invoice" },
  ];

  if (associated_dealers.length > 1) {
    tableHeaders.splice(1, 0, {
      label: "Associated Dealer",
      key: "dealer_alias",
    });
  }

  return (
    <div className="container mb-10">
      <TableGrid
        data={data}
        loading={loading}
        handleViewButtonClick={(data) => setIsOpen({ show: true, data })}
        searchComponentProps={{
          onSearchSubmit: (values, perPage) => loadData(1, perPage, values),
          onClearFilter: (perPage) => loadData(1, perPage, null, true),
          searchFields: [
            ...(associated_dealers.length > 1
              ? ([
                  {
                    label: "Associated Dealer",
                    name: "dealer_id",
                    type: "select",
                    defaultValue: { label: "All", value: "all" },
                    options: [
                      { label: "All Associated Dealer", value: "all" },
                      ...associated_dealers.map((d: any) => ({
                        label: d.alias,
                        value: d.id,
                      })),
                    ],
                  },
                ] as any)
              : []),
            {
              label: "Contract Number",
              name: "contract_number",
              type: "text",
            },
            {
              label: "Customer First Name",
              name: "customer_first_name",
              type: "text",
            },
            {
              label: "Customer Last Name",
              name: "customer_last_name",
              type: "text",
            },
            {
              label: "Customer Phone",
              name: "customer_phone",
              type: "phone",
            },
            {
              label: "Customer Email",
              name: "customer_email",
              type: "email",
            },
            {
              label: "Customer ID",
              name: "customer_id",
              type: "text",
            },
            {
              label: "Store Location",
              name: "store_location",
              type: "text",
            },
            {
              label: "Invoice",
              name: "invoice_number",
              type: "text",
            },
            {
              label: "Purchase Date",
              name: "contract_purchase_date",
              type: "date",
            },
          ],
        }}
        loadMoreData={(per_page) => {
          const nextPage = info?.page + 1;
          if (info?.more_records) {
            loadData(nextPage, per_page);
          }
        }}
        onPerPageChange={(per_page) => loadData(1, per_page)}
        headers={tableHeaders}
      />
      <ContractViewModal
        data={isOpen.data}
        isOpen={isOpen.show}
        close={close}
        statusbg={statusbg}
      />
    </div>
  );
}

const mapStateProps = (state: RootState) => {
  return {
    data: state.user.contracts?.data || [],
    info: state.user.contracts?.info || {},
    loading: isScopeLoading(state.scopeLoading, `${UserScope}-contracts`),
    associated_dealers: state.user.selectedOrg.associated_dealers || [],
  };
};

export default connect(mapStateProps)(Contracts);
